'use client';

import { ReactNode, useRef, useEffect, useState } from 'react';

/**
 * Properties for the ElementVisibilityChecker component.
 */
interface IElementVisibilityCheckerProps {
  /** The content to be monitored for visibility. */
  children: ReactNode;
  /** Optional CSS class name for the container. */
  className?: string;
  /** Callback function triggered when the visibility state changes. */
  onVisibleChange: (isVisible: boolean) => void;
}

/**
 * -----------------------------------------------------------------------------
 * This tries to show the visibility of an element by using IntersectionObserver
 * and checking if the element is visible or not.
 */
export function ElementVisibilityChecker({
  children,
  className,
  onVisibleChange,
}: IElementVisibilityCheckerProps) {
  const targetRef = useRef(null);

  /**
   * NOTE: Not using the `isVisible` state for now. Will be picked up later
   * if a need arises to expose this to the parent.
   */
  const [, setIsVisible] = useState(false);

  useEffect(() => {
    const targetNode = targetRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const updatedIsVisible = entry?.isIntersecting || false;

        setIsVisible(updatedIsVisible);
        onVisibleChange(updatedIsVisible);
      },
      {
        root: null, // viewport
        rootMargin: '0px', // no margin
        threshold: 0.5, // 50% of target visible
      },
    );

    if (targetNode) {
      observer.observe(targetNode);
    }

    return () => {
      if (targetNode) {
        observer.unobserve(targetNode);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={targetRef} className={className}>
      {children}
    </div>
  );
}
